import Vue from 'vue';
import { ProductName } from '@/helpers/accountsIframe';

export interface ProuniLoginParams {
  onSuccess?: () => void;
  onError?: () => void;
}

export interface PocketProfile {
  id?: string | number;
  name?: string;
  email?: string;
  phone?: string;
  d?: string;
}

/**
 * Simplified ProUni authentication: ONLY the Descomplica login via pocket.
 * There is no admissions-auth-token exchange. The session lives in the cookie
 * `d` (`POCKET.profile()`), which is later sent as `Bearer <d>` on ProUni
 * GraphQL calls.
 */
const CONTAINER_CLASS = 'authIframeContainer';
const POCKET_READY_TIMEOUT_MS = 8000;
const POCKET_READY_POLL_MS = 100;

// pocket.latest.js is loaded with `async` in index.html, so on a direct hit
// to /prouni (the pipeline's entry route) window.POCKET may not exist yet
// when this component mounts. Poll for it instead of failing silently.
const waitForPocket = (timeoutMs = POCKET_READY_TIMEOUT_MS): Promise<boolean> =>
  new Promise(resolve => {
    if (window.POCKET) {
      resolve(true);
      return;
    }

    const startedAt = Date.now();
    const interval = window.setInterval(() => {
      if (window.POCKET) {
        window.clearInterval(interval);
        resolve(true);
      } else if (Date.now() - startedAt >= timeoutMs) {
        window.clearInterval(interval);
        resolve(false);
      }
    }, POCKET_READY_POLL_MS);
  });

const getTokenD = (): string | undefined => {
  if (window.POCKET && window.POCKET.profile) {
    const profile = window.POCKET.profile();
    if (profile && profile.d) return profile.d;
  }

  const cookieToken = Vue.$cookies.get('d');
  return cookieToken ? String(cookieToken).replace(/"/g, '') : undefined;
};

const isLogged = (): boolean => Boolean(getTokenD());

const loginWithIframe = async (
  { onSuccess, onError }: ProuniLoginParams,
  elementToEmbedIn: string,
  classToAdd: string,
): Promise<void> => {
  const isPocketReady = await waitForPocket();
  if (!isPocketReady) {
    if (onError) onError();
    return;
  }

  window.POCKET.Auth.loginWithIframe({
    url: process.env.VUE_APP_ACCOUNTS_URL,
    productName: ProductName.UNDERGRAD_DESCOMPLICA,
    hideHeaders: true,
    hideFooter: false,
    hideSocialMedia: true, // hide social media buttons in the iframe because it's not working as expected
    onSuccess,
    onError,
  } as any);

  const authIframeContainer = document.querySelector(`.${CONTAINER_CLASS}`);
  const content = document.querySelector(elementToEmbedIn);

  if (content && authIframeContainer) {
    authIframeContainer.classList.add(classToAdd);
    content.appendChild(authIframeContainer);
  }
};

const getPocketProfile = (): PocketProfile | null => {
  if (window.POCKET && window.POCKET.profile) {
    const profile = window.POCKET.profile();
    return profile || null;
  }
  return null;
};

const getProfileName = (): string | undefined => {
  if (window.POCKET && window.POCKET.profile) {
    const profile = window.POCKET.profile();
    return profile ? profile.name : undefined;
  }
  return undefined;
};

const logout = (): void => {
  if (window.POCKET && window.POCKET.profile) {
    window.POCKET.profile.logout();
  }
};

export default {
  getTokenD,
  isLogged,
  loginWithIframe,
  getPocketProfile,
  getProfileName,
  logout,
};
